import localeCode from 'locale-code';
import getCountryISO3 from 'country-iso-2-to-3';

import { apiBaseUrl } from '../services/apiBaseUrl';

const isFullUrl = (url: string): boolean => /^(http:\/\/|https:\/\/)/.test(url);

const parseLegacyUrl = (url: string): { container: string; name: string } => {
  const regex = /api\/v3\/Containers\/([^/]+)\/[^/]+\/([^/]+)/;
  const match = url.match(regex);
  const [, container, name] = match || [];
  return { container: container || '', name: name || '' };
};

const parseInternalAsset = (
  urlLike: string
): { container: string; name: string } => {
  const parts = urlLike.split('/');
  if (parts.length < 2) {
    return { container: '', name: '' };
  }
  const container = parts[0];
  const name = parts.slice(1).join('/');
  return { container, name };
};

export const getAssetUrl = (
  downloadMethod: string,
  url: string,
  size?: string | null
): string => {
  // if it's a full URL and not a pi-top image one, leave as is
  // if it's a pi-top image url, pick out the container and name
  // build container and name ones into a new URL, with size
  // if  it doesn't match, return empty string

  if (!url) return '';

  // legacy url might be eg 'https://backend-staging.pi-top.com/api/v3/Containers/12345/downloadLol/somefile.txt'
  const isLegacyContainerUrl = url.includes('api/v3/Containers/');

  //  return external URLs as is
  if (isFullUrl(url) && !isLegacyContainerUrl) return url;

  const { container, name } = isLegacyContainerUrl
    ? parseLegacyUrl(url)
    : parseInternalAsset(url);

  if (!container || !name) return '';

  // if size is provided, append it to the name
  const nameParts = name.split('.');
  const ext = nameParts.length > 1 ? `.${nameParts.pop()}` : '';
  const sizedName = `${nameParts.join('.')}${size ? `x${size}` : ''}${ext}`;

  return `${apiBaseUrl}/Containers/${container}/${downloadMethod}/${sizedName}`;
};

export const getYoutubeUrl = (
  youtubeId: string,
  { language, showCaptions }: { language?: string; showCaptions?: boolean } = {}
): string => {
  if (!youtubeId) return '';

  const baseUrl = `https://www.youtube-nocookie.com/embed/${youtubeId}`;
  const languageCode = localeCode.getLanguageCode(language || '');

  // https://developers.google.com/youtube/player_parameters#Parameters
  const searchParams = new URLSearchParams({
    cc_load_policy: showCaptions ? '1' : '0',
  });
  if (languageCode) {
    searchParams.append('cc_lang_pref', languageCode);
    searchParams.append('hl', languageCode);
  }

  return `${baseUrl}?${searchParams.toString()}`;
};

// Code get from: https://gist.github.com/takien/4077195
export const getYoutubeId = (youtubeValue: string): string => {
  let youtubeId: string;

  const parsedYoutubeValue = youtubeValue
    .replace(/(>|<)/gi, '')
    .split(
      /^.*(?:(?:youtu\.?be(\.com)?\/|v\/|vi\/|u\/\w\/|embed\/|shorts\/)|(?:(?:watch)?\?v(?:i)?=|&v(?:i)?=))([^#&?]*).*/
    );
  if (parsedYoutubeValue[2] !== undefined) {
    [youtubeId] = parsedYoutubeValue[2].split(/[^0-9a-z_-]/i);
  } else {
    youtubeId = youtubeValue;
  }

  return youtubeId;
};

export const getIsYoutubeIdValid = (youtubeId: string): boolean => {
  // Regex taken from: https://stackoverflow.com/a/4084332
  return !!youtubeId && /^[a-zA-Z0-9_-]{11}$/.test(youtubeId);
};

export const getWistiaUrl = (
  wistiaId: string,
  {
    language,
    showCaptions = false,
  }: { language?: string; showCaptions?: boolean } = {}
): string => {
  if (!wistiaId) return '';

  const baseUrl = `https://fast.wistia.net/embed/iframe/${wistiaId}`;

  const searchParams = new URLSearchParams({
    'plugin[captions-v1][onByDefault]': showCaptions.toString(),
  });

  if (language) {
    const rawCountryCode = localeCode.getCountryCode(language || '');
    const iso3CountryCode = getCountryISO3(rawCountryCode);

    searchParams.append(
      'plugin[captions-v1][language]',
      (iso3CountryCode || rawCountryCode).toLowerCase()
    );
  }

  return `${baseUrl}?${searchParams.toString()}`;
};

export const getWistiaId = (value: string): string => {
  // capture wistia id from: https://fast.wistia.net/embed/iframe/hto1lacefi
  const iframeEmbedValue = value.match(/\/embed\/iframe\/([a-zA-Z0-9]*)/);
  if (iframeEmbedValue) return iframeEmbedValue[1];

  // capture wistia id from: https://*.wistia.net/medias/hto1lacefi
  const wistiaMediasValue = value.match(/wistia.*\/medias\/([a-zA-Z0-9]*)/);
  if (wistiaMediasValue) return wistiaMediasValue[1];

  return value;
};

export const getIsWistiaIdValid = (wistiaId: string): boolean => {
  return /^[a-zA-Z0-9]{10}$/.test(wistiaId);
};

export const getImageUrl = (url: string, size?: string | null): string =>
  getAssetUrl('downloadCDN', url, size);
export const getFileUrl = (url: string): string =>
  getAssetUrl('downloadCDN', url);
export const getVideoUrl = (url: string): string =>
  getAssetUrl('downloadCDN', url);
